import {
  CreateWorkflowNode,
  PythonFunctionConfiguration,
  WorkflowEdge,
  WorkflowNode,
  WorkflowNodeType,
} from 'api/model';
import { useStore } from 'store/store';
import { useMemo, useState } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { SectionTitle } from 'components/form/SectionTitle';
import { CodePreviewPaper } from './Form.styles';
import { useParams } from 'react-router';
import { useNodeValidation } from 'hooks/useNodeValidation.tsx';
import PythonFunctionNodeDraggableRow from './components/PythonFunctionNodeDraggableRow.tsx';
import FormBlock from 'components/form/FormBlock.tsx';
import { NodeFormProps } from 'api/workflows-steps/formSchema.tsx';
import usePythonFunctions from 'api/python-functions/usePythonFunctions';
import usePythonFunction from 'api/python-functions/usePythonFunction';
import CodeEditor from 'components/codeEditor/CodeEditor';
import clsx from 'clsx';

const DEFAULT_NEW_FUNCTION_REVISION = 1;

type Predecessor = {
  node_id: string;
  type: WorkflowNodeType;
  name: string;
};

const reorder = (list: Predecessor[], startIndex: number, endIndex: number): Predecessor[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const PythonFunctionForm = (props: NodeFormProps) => {
  const { nodeId, initialValues = {} as CreateWorkflowNode } = props;
  const { mainReactFlowInstance } = useStore();
  const { projectId } = useParams();
  const { functions } = usePythonFunctions(projectId ?? null, true);

  const [formValues, setFormValues] = useState<CreateWorkflowNode>(initialValues);

  const { validate } = useNodeValidation(initialValues.type);

  const selectedConfig = formValues.configuration as PythonFunctionConfiguration | undefined;
  const { pythonFunction: currentFunction } = usePythonFunction({
    functionId: selectedConfig?.function_id ?? '',
    revision: selectedConfig?.function_revision,
  });

  const handleFormChange = (data: CreateWorkflowNode | WorkflowNode) => {
    const newConfiguration = data.configuration as PythonFunctionConfiguration | undefined;
    if (!newConfiguration) {
      setFormValues(data);
      props.onChange?.(data);
      return;
    }

    const previousConfiguration = formValues.configuration as PythonFunctionConfiguration | undefined;
    const functionId = newConfiguration.function_id;
    if (!functionId) {
      newConfiguration.function_revision = undefined;
    } else if (functionId !== previousConfiguration?.function_id) {
      const selectedFunction = functions?.find((fn) => fn.id === functionId);
      newConfiguration.function_revision = selectedFunction ? selectedFunction.revision : DEFAULT_NEW_FUNCTION_REVISION;
    }

    const updatedFormData = { ...data, configuration: newConfiguration };
    setFormValues(updatedFormData);
    props.onChange?.(updatedFormData);
  };

  const predecessors: Predecessor[] = useMemo(() => {
    const order = (formValues.configuration as PythonFunctionConfiguration)?.node_order;

    return (
      mainReactFlowInstance
        ?.getEdges()
        .filter((e) => e.target === nodeId)
        .map((e) => mainReactFlowInstance.getNode(e.source))
        .filter(Boolean)
        .sort((a, b) => {
          if (!order) {
            return 0;
          }
          const orderA = order.indexOf(a!.id);
          const orderB = order.indexOf(b!.id);

          if (orderA === -1 && orderB === -1) return 0;
          if (orderA === -1) return 1;
          if (orderB === -1) return -1;

          return orderA - orderB;
        })
        .map((predecessor) => ({
          node_id: predecessor!.id,
          type: predecessor!.type as WorkflowNodeType,
          name: predecessor!.data.name,
        })) ?? []
    );
  }, [nodeId, mainReactFlowInstance, formValues]);

  const [items, setItems] = useState<Predecessor[]>(predecessors);

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    const newItems = reorder(items, result.source.index, result.destination.index);
    setItems(newItems);
  };

  const handleSubmit = async () => {
    const configuration = {
      ...formValues.configuration,
      node_order: items.map((item) => item.node_id),
    } as PythonFunctionConfiguration;

    const formData = { ...formValues, configuration };
    const isCreate = !initialValues?.name;
    if (!isCreate) {
      const validationExtraData = {
        edges: mainReactFlowInstance?.getEdges().filter((e) => e.target === formData.id) as WorkflowEdge[],
      };

      const isValid = await validate(formData, validationExtraData);
      if (!isValid) {
        return;
      }
    }
    props.onSubmit?.(formData);
  };

  const formContext = { ...props.formContext, pythonFunctionInputCount: Math.max(predecessors.length, 1) };

  return (
    <div className="flex flex-col gap-2">
      <FormBlock {...props} formContext={formContext} onSubmit={handleSubmit} onChange={handleFormChange} />
      {currentFunction?.function_code && (
        <div className="flex flex-col gap-1">
          <SectionTitle title="Function code (read-only)" />
          <CodePreviewPaper>
            <CodeEditor
              key={`${currentFunction.id}-${currentFunction.revision}`}
              language="python"
              height="30vh"
              value={currentFunction.function_code}
              readOnly
              onChange={() => undefined}
            />
          </CodePreviewPaper>
        </div>
      )}
      {predecessors.length > 1 && (
        <div>
          <SectionTitle title="Node order" />
          <div
            className={clsx('flex flex-col gap-2 p-4', {
              'pointer-events-none': props.disabled,
            })}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12 items-center font-bold">
              <span className="col-span-1 text-center"></span>
              <span className="col-span-4 text-center">dataframe order</span>
              <span className="col-span-6 text-center">node name</span>
              <span className="col-span-1 text-center"></span>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable" isDropDisabled={props.disabled}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="w-full">
                    {items.map((item, index) => (
                      <PythonFunctionNodeDraggableRow
                        key={item.node_id}
                        id={item.node_id}
                        index={index}
                        name={item.name}
                        type={item.type}
                        disabled={props.disabled}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      )}
    </div>
  );
};

export default PythonFunctionForm;
